import { Component, OnInit } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import { PageComponent } from '../shared/components/page/page.component';
import { DashboardService } from './dashboard.service';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { AuthService } from '@app/core/auth';
import { Router } from '@angular/router';
import { APP_ROUTES } from '@app/constants';
import { GenericResponse } from '@app/core/backend';
import {
  CardGenTypeCount,
  CardStatusCount,
  CardTypeCount,
} from '@app/modules/dashboard/dashboard.type';
import { DatePipe, NgClass } from '@angular/common';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { Dialog, DialogModule } from 'primeng/dialog';
import {Chip} from "primeng/chip";
import { Notice } from '../configuration/model/notice.model';
import { DialogService } from 'primeng/dynamicdialog';
import { NoticeListViewDetailDialogComponent } from '../configuration/notice/notice-list-detail-dialog/notice-list-detail-dialog';

@Component({
  selector: 'app-dashboard',

  imports: [
    ChartModule,
    PageComponent,
    NgClass,
    TableModule,
    DatePipe,
    DialogModule,
    Chip,
    ButtonModule
],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit {
  dashboardData: any;

  year: number = new Date().getFullYear();

  dashboardNoticeData: any;

  pageSize: number = 5;
  currentPage: number = 1;
  first: number = this.pageSize * this.currentPage - this.pageSize;
  loading: boolean = false;
  totalRecords: number = 0;
  private isInitialNoticeLoad: boolean = true;

  polarData: any;

  piechartData: any;

  pichartDataTotal!: number;

  lineOptions: any;

  pieOptions: any;

  subscription?: Subscription;

  userName?: string | null;

  lineChartData: any;

  lineChartOptions: any;
  displayDialog: boolean = false; // Control dialog visibility
  notifications: any[] = []; // Store notifications

  constructor(
    private readonly authService: AuthService,
    private readonly dashBoardService: DashboardService,
    private readonly messageService: MessageService,
    private readonly router: Router,
     public readonly dialogService: DialogService
  ) {}

  ngOnInit() {
    this.authService.user$.subscribe((user) => {
      this.userName = user.username;
    });
    this.loadDashboardData();
  }

  closeDialog() {
    this.displayDialog = false; // Close dialog
  }

  handleDataOnPageChange(event: any) {
    this.currentPage = event.first / this.pageSize + 1;
    if (this.pageSize !== event.rows) {
      this.currentPage = 1;
      event.first = 0;
    }
    this.pageSize = event.rows;
  }

  handleLoadData(event: TableLazyLoadEvent) {
    this.loadDashboardNoticeData(event);
  }

  initCharts() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue(
      '--text-color-secondary'
    );
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    this.lineChartData = {
      labels: this.dashboardData?.cardMonthlyCounts.map((x: any) =>
        this.getMonthName(x.month)
      ),
      datasets: [
        {
          label: 'Active Card',
          fill: false,
          borderColor: documentStyle.getPropertyValue('--green-500'),
          yAxisID: 'y',
          tension: 0.4,
          data: this.dashboardData?.cardMonthlyCounts.map((x: any) => x.active),
        },
        {
          label: 'Blocked Card',
          fill: false,
          borderColor: documentStyle.getPropertyValue('--yellow-500'),
          yAxisID: 'y1',
          tension: 0.4,
          data: this.dashboardData?.cardMonthlyCounts.map(
            (x: any) => x.blocked
          ),
        },
        {
          label: 'Expired Card',
          fill: false,
          borderColor: documentStyle.getPropertyValue('--red-500'),
          yAxisID: 'y1',
          tension: 0.4,
          data: this.dashboardData?.cardMonthlyCounts.map(
            (x: any) => x.expired
          ),
        },
      ],
    };

    this.lineChartOptions = {
      stacked: false,
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            color: textColor,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            drawOnChartArea: false,
            color: surfaceBorder,
          },
        },
      },
    };

    this.lineOptions = {
      plugins: {
        legend: {
          labels: {
            fontColor: textColor,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false,
          },
        },
        y: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false,
          },
        },
      },
    };

    this.polarData = {
      datasets: [
        {
          data: [11, 16, 7, 3],
          backgroundColor: [
            'red',
            'maroon',
           'blue',
            'green',
          ],
          label: 'My dataset',
        },
      ],
      labels: ['PENDING', 'APPROVED', 'SENT-TO-SWITCH', 'ACTIVATED'],
    };

    const hasData = this.dashboardData?.cardTypeCounts?.some((item: CardTypeCount) => item.count > 0);

    this.piechartData = {
      labels: hasData
        ? this.dashboardData?.cardTypeCounts?.map((item: CardTypeCount) => item?.cardType)
        : ['No Data'],
      datasets: [{
        data: hasData
          ? this.dashboardData?.cardTypeCounts.map((item: CardTypeCount) => item.count)
          : [1], // Show a single black slice when there's no data
        backgroundColor: hasData
          ? [
            'red',
            'Orange',
            'green',
            'teal',
          ]
          :  [documentStyle.getPropertyValue('--blue-700')], // Black color when no data
        hoverBackgroundColor: hasData
          ? [
            'maroon',
            'yellow',
            'teal',
            'Olive',
          ]
          :  [documentStyle.getPropertyValue('--blue-600')]// Black hover color when no data
      }]
    };

/*    this.piechartData = {
      labels: this.dashboardData?.cardTypeCounts?.map((item: CardTypeCount) => item?.cardType),
      datasets: [{
        data: this.dashboardData?.cardTypeCounts.map((item: CardTypeCount) => item.count),
        backgroundColor: [
          // documentStyle.getPropertyValue('--blue-200'),
          documentStyle.getPropertyValue('--blue-700'),
          documentStyle.getPropertyValue('--red-500'),
          documentStyle.getPropertyValue('--green-500')
        ],
        hoverBackgroundColor: [
          // documentStyle.getPropertyValue('--blue-400'),
          documentStyle.getPropertyValue('--blue-600'),
          documentStyle.getPropertyValue('--red-400'),
          documentStyle.getPropertyValue('--green-400')
        ]
      }

      ]
    }*/
    this.pichartDataTotal = this.dashboardData?.cardTypeCounts?.reduce((sum: number, item: CardTypeCount) => sum + item.count, 0);

    this.pieOptions = {
      plugins: {
        legend: {
          labels: {
            usePointStyle: true,
            fontColor: textColor,
          },
        },
      },
    };
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  get greetingText(): string {
    let interval: string;
    const time = new Date().getHours();
    if (time >= 0 && time < 12) {
      interval = 'Morning';
    } else if (time >= 12 && time < 16) {
      interval = 'Afternoon';
    } else {
      interval = 'Evening';
    }
    return `Good ${interval}`;
  }

  handleNotice() {
    this.router.navigate([APP_ROUTES.CONFIGURATION, APP_ROUTES.NOTICE]).then();
  }

  loadDashboardData() {
    this.dashBoardService.getDashboardData().subscribe({
      next: (response) => {
        this.dashboardData = response.data;
        this.initCharts();
      },
      error: (errorResponse) => {
        let errorData: GenericResponse<any> = errorResponse.error;
        if (errorResponse && errorData?.message) {
          this.messageService.add({
            key: 'message',
            severity: 'error',
            summary: '',
            detail: errorData.message,
          });
        } else
          this.messageService.add({
            key: 'message',
            severity: 'error',
            summary: '',
            detail:
              'Dashboard Information loading error. Please try again later.',
          });
      },
    });
  }

  loadDashboardNoticeData(event: any) {
    this.dashBoardService
      .loadDashboardNoticeData(this.currentPage, this.pageSize, event)
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.currentPage = response.data.pageNo;
          this.totalRecords = response.data.totalElements;
          this.pageSize = response.data.pageSize;
          this.first = this.pageSize * this.currentPage - this.pageSize;
          this.dashboardNoticeData = response.data.data;
          if (this.isInitialNoticeLoad && this.dashboardNoticeData?.length > 0) {
            this.notifications = this.dashboardNoticeData;
            this.displayDialog = true;
            this.isInitialNoticeLoad = false;
          }
        },
        error: (errorResponse) => {
          this.loading = false;
          let errorData: GenericResponse<any> = errorResponse.error;
          if (errorResponse && errorData?.message) {
            this.messageService.add({
              key: 'message',
              severity: 'error',
              summary: '',
              detail: errorData.message,
            });
          } else
            this.messageService.add({
              key: 'message',
              severity: 'error',
              summary: '',
              detail:
                'Dashboard  Notice Information loading error. Please try again later.',
            });
        },
      });
  }

  getCountByCardGenType(cardGenType: string) {
    return this.dashboardData?.cardGenTypeCounts
      ?.filter((item: CardGenTypeCount) => item.cardGenType == cardGenType)
      .map((x: CardGenTypeCount) => x.currentCount);
  }

  getPreviousCountByCardGenType(cardGenType: string) {
    return this.dashboardData?.cardGenTypeCounts
      ?.filter((item: CardGenTypeCount) => item.cardGenType == cardGenType)
      .map((x: CardGenTypeCount) => x.previousCount);
  }

  getTotalActiveCardsByCardGenType() {
    return this.dashboardData?.cardGenTypeCounts?.reduce(
      (sum: number, x: CardGenTypeCount) => sum + x.currentCount,
      0
    );
  }

  getPreviousTotalActiveCardsByCardGenType() {
    return this.dashboardData?.cardGenTypeCounts?.reduce(
      (sum: number, x: CardGenTypeCount) => sum + x.previousCount,
      0
    );
  }

  getChangePercentageActive() {
    const currentCount: number = this.getTotalActiveCardsByCardGenType();
    const previousCount: number =
      this.getPreviousTotalActiveCardsByCardGenType();
    let changePercentage: number = this.getChangePercentageCore(
      currentCount,
      previousCount
    );

    const formattedPercentage =
      changePercentage >= 0
        ? '+' + changePercentage + '%'
        : changePercentage + '%';

    return `<span class="${this.getChangeColorCore(
      changePercentage
    )}">${formattedPercentage}</span>`;
  }

  getChangePercentageForCardGenType(cardGenType: string): string {
    const currentCount: number = this.getCountByCardGenType(cardGenType);
    const previousCount: number =
      this.getPreviousCountByCardGenType(cardGenType);
    let changePercentage: number = this.getChangePercentageCore(
      currentCount,
      previousCount
    );

    const formattedPercentage =
      changePercentage >= 0
        ? '+' + changePercentage + '%'
        : changePercentage + '%';

    return `<span class="${this.getChangeColorCore(
      changePercentage
    )}">${formattedPercentage}</span>`;
  }

  getChangePercentageCore(currentCount: number, previousCount: number) {
    let changePercentage: number;

    if (previousCount == 0) {
      if (currentCount == 0) {
        changePercentage = 0;
      } else {
        changePercentage = 100;
      }
    } else {
      changePercentage = ((currentCount - previousCount) / previousCount) * 100;
    }

    return changePercentage;
  }

  getChangeColorCore(changePercentage: number) {
    let changeColor: string = 'text-grey-200'; // Default color
    if (changePercentage >= 0) {
      changeColor = 'text-green-500'; // Green for positive change
    } else if (changePercentage < 0) {
      changeColor = 'text-red-500'; // Red for negative change
    }

    return changeColor;
  }

  getTotalCardEntry() {
    return this.dashboardData?.cardStatusCounts.reduce(
      (sum: number, x: CardStatusCount) => sum + x.count,
      0
    );
  }
openDialog(notice: Notice) {
    if (notice) {
      // this.selectedNotice = notice; // Set the selected notice to the clicked one
      // this.isDialogVisible = true; // Show the dialog
       const ref = this.dialogService.open(
         NoticeListViewDetailDialogComponent,
         {
           data: notice,
           //header: 'Are you sure you want to proceed ?',
           width: "50%",
           contentStyle: { overflow: "auto" },
           baseZIndex: 10000,
           modal: true,
           closable: true,
           maximizable: true,
         }
       );
    }
  }
  formatStatus(status: string): string {
    return status
      ?.toLowerCase()
      ?.split('_') // Split by underscores
      ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      ?.join(' '); // Join with spaces
  }

  getStatusCountColor(status: string): string {
    const colors: Record<string, string> = {
      PENDING: 'bg-yellow-600',
      CHARGE_FAILED: 'bg-red-600',
      ACTIVATED: 'bg-blue-600',
      APPROVED: 'bg-green-600',
      SENT_TO_SWITCH: 'bg-purple-600',
      PIN_DELIVERED: 'bg-gray-600',
    };
    return colors[status] || 'bg-gray-400';
  }

  getStatusCountPercentage(count: number) {
    return (count / this.getTotalCardEntry()) * 100;
  }

  getMonthName(month: string): string {
    const months: Record<string, string> = {
      '01': 'January',
      '02': 'February',
      '03': 'March',
      '04': 'April',
      '05': 'May',
      '06': 'June',
      '07': 'July',
      '08': 'August',
      '09': 'September',
      '10': 'October',
      '11': 'November',
      '12': 'December',
    };
    return months[month] || 'Invalid Month';
  }

  markAsRead(notice: any) {
    if (!notice.isRead) {
      this.dashBoardService.markAsRead(notice?.id).subscribe({
        next: (response) => {
          this.loading = false;
          notice.isRead = true;
        },
        error: (errorResponse) => {
          this.loading = false;
          let errorData: GenericResponse<any> = errorResponse.error;
          if (errorResponse && errorData?.message) {
            this.messageService.add({
              key: 'message',
              severity: 'error',
              summary: 'Error',
              detail: errorData.message,
            });
          } else
            this.messageService.add({
              key: 'message',
              severity: 'error',
              summary: 'Error',
              detail: 'Dashboard  Notice Information reading error.',
            });
        },
      });
    }
  }
}
